import { ReactNode, ErrorInfo } from 'react';
import { ErrorBoundary, FallbackProps } from 'react-error-boundary';
import './ErrorBoundary.scss';
import { useLogger } from '../../core/services/telemetry';

interface TelemetryErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

const DefaultErrorFallback = (_props: FallbackProps) => (
  <div className='error-boundary'>
    <h2>Something went wrong.</h2>
    <p>The error has been reported to our team.</p>
  </div>
);

const TelemetryErrorBoundary = ({
  children,
  fallback,
  componentName = 'UnknownComponent',
}: TelemetryErrorBoundaryProps) => {
  const logger = useLogger();

  const handleError = (error: unknown, info: ErrorInfo) => {
    const normalizedError = error instanceof Error ? error : new Error(String(error));

    // App Insights (first-party) gets the full componentStack, unchanged.
    // componentStack carries no PII by design (component display names + source
    // locations, never props/state); the Datadog forward still length-caps long
    // string properties via sanitizeTelemetryProperties (useLogger), so the
    // third-party payload stays bounded without altering the App Insights write.
    logger.error('Error caught by TelemetryErrorBoundary', normalizedError, {
      source: 'TelemetryErrorBoundary',
      componentName,
      componentStack: info.componentStack,
    });
  };

  return (
    <ErrorBoundary
      FallbackComponent={fallback ? () => <>{fallback}</> : DefaultErrorFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export default TelemetryErrorBoundary;
